import { useMemo } from 'react';

import { TASK_CONST } from '@/constant';
import {
  useGetProjectWorktimeQuery,
  useProjectFrameStateQuery,
  useProjectObjectQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  Groups,
  History,
  RateReview,
  RestartAlt,
  Schedule,
  TaskAlt,
  VFlex,
  VGrid,
  VStack,
  VideoLabel,
} from '@/package';
import type { FrameStateStatistic, Project } from '@/type';
import { makeAvgTime } from '@/util';

import {
  StatisticSummaryCard,
  calculateTotalFrame,
  calculateTotalObject,
  calculateUserCount,
} from '../Statistic';
import { DoughnutChart } from '../Statistic/DoughnutChart';

export const ProjectStatisticSummary = ({ project }: { project: Project }) => {
  const { data: projectObject } = useProjectObjectQuery(project.id, true);
  const { data: projectState } = useProjectFrameStateQuery(project.id);
  const { data: projectWorktime } = useGetProjectWorktimeQuery({
    projectID: project.id,
  });

  const frameProgress = (projectState as FrameStateStatistic) ?? {
    prework: 0,
    working: 0,
    reject: 0,
    review: 0,
    reviewing: 0,
    done: 0,
  };
  const taskProgress = project.task?.reduce(
    (acc, cur) => {
      return {
        none: acc.none + (cur.state === TASK_CONST.TASK_WORKSTATE.NONE ? 1 : 0),
        pending:
          acc.pending +
          (cur.state === TASK_CONST.TASK_WORKSTATE.PENDING ? 1 : 0),
        ready:
          acc.ready + (cur.state === TASK_CONST.TASK_WORKSTATE.READY ? 1 : 0),
        progress:
          acc.progress +
          (cur.state === TASK_CONST.TASK_WORKSTATE.PROGRESS ? 1 : 0),
        completed:
          acc.completed +
          (cur.state === TASK_CONST.TASK_WORKSTATE.COMPLETED ? 1 : 0),
        fail: acc.fail + (cur.state === TASK_CONST.TASK_WORKSTATE.FAIL ? 1 : 0),
      };
    },
    { none: 0, pending: 0, ready: 0, progress: 0, completed: 0, fail: 0 },
  ) ?? { none: 0, pending: 0, ready: 0, progress: 0, completed: 0, fail: 0 };
  const totalTaskCount = project.task?.length ?? 0;
  const totalFrameCount = calculateTotalFrame(frameProgress);
  const totalObjectCount = calculateTotalObject(projectObject?.statExtendClass);
  const totalUserCount = calculateUserCount(
    project?.user,
    (cur: { role: string }) => cur.role,
  );

  const avgTime = useMemo(() => {
    if (!projectWorktime?.[0]) {
      return {
        work: '0m 0s',
        review: '0m 0s',
        rework: '0m 0s',
      };
    }

    return makeAvgTime(projectWorktime[0]);
  }, [projectWorktime]);

  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Summary</div>

      <VGrid cols={4} gap={1}>
        <StatisticSummaryCard
          Icon={TaskAlt}
          content={() => Intl.NumberFormat().format(totalTaskCount)}
          title="Total Tasks"
        />
        <StatisticSummaryCard
          Icon={VideoLabel}
          content={() => Intl.NumberFormat().format(totalFrameCount)}
          title="Total Work Frames"
        />

        <VStack
          gap={0.5}
          className="col-span-2 row-span-2 rounded-md px-4 py-2 shadow-sm"
        >
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total tasks progress
            </VFlex>

            <div className="text-body-sm text-gray-600">
              Total {Intl.NumberFormat().format(totalTaskCount)}
            </div>
          </VFlex>
          <VFlex className="w-full justify-center">
            <DoughnutChart
              totalTask={totalTaskCount}
              taskProgress={taskProgress}
            />
          </VFlex>
        </VStack>

        <StatisticSummaryCard
          Icon={CenterFocusWeak}
          content={() => Intl.NumberFormat().format(totalObjectCount)}
          title="Total Objects"
        />

        <StatisticSummaryCard
          Icon={Groups}
          content={() => (
            <VFlex
              className="text-body-md text-gray-400"
              items="center"
              gap={0.25}
            >
              <span className="font-normal">Manager</span>
              <span className="text-body-lg font-semibold text-black">
                {Intl.NumberFormat().format(totalUserCount.manager)}
              </span>

              <div className="h-4 w-px bg-gray-300" />

              <span className="font-normal">Labeler</span>
              <span className="text-body-lg font-semibold text-black">
                {Intl.NumberFormat().format(totalUserCount.worker)}
              </span>
            </VFlex>
          )}
          title="Members"
        />

        <StatisticSummaryCard
          Icon={Schedule}
          content={() => avgTime.work}
          title="Avg Work Time"
        />

        <StatisticSummaryCard
          Icon={RateReview}
          content={() => avgTime.review}
          title="Avg Review Time"
        />

        <StatisticSummaryCard
          Icon={RestartAlt}
          content={() => avgTime.rework}
          title="Avg Rework Time"
        />
      </VGrid>
    </VStack>
  );
};

ProjectStatisticSummary.displayName = 'ProjectStatisticSummary';
